import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Chip, CircularProgress, ThemeProvider } from '@mui/material';
import StarRateIcon from '@mui/icons-material/StarRate';
import CloseIcon from '@mui/icons-material/Close';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';
import { maxSymbolQTY, minQtyTopSkills, qtyTopSkills, validationMessages } from '../../../../utils/constants';
import { addToSkills, theme } from '../helpers/helpers';
import ValidationErrorLabel from '../../ValidationErrorLabel/ValidationErrorLabel';
import api from '../../../../api/apiSettings';
import classes from '../scss/addEditFormModal.module.scss';

function SkillsForm(props) {
  const { addEditForm } = props;

  const [skillSymbolQty, setSkillSymbolQty] = useState(0);
  const [valuesToAdd, setValuesToAdd] = useState([]);
  const [skillsFieldTouched, setSkillsFieldTouched] = useState(false);

  const [suggestions, setSuggestions] = useState([{ label: '', value: 0 }]);

  const [open, setOpen] = React.useState(false);
  const loading = open && suggestions?.length === 0;

  const getQtyTopSkills = useCallback(
    () => addEditForm.values.skills?.filter((skill) => skill.TopSkill).length,
    [addEditForm.values.skills]
  );
  const { t } = useTranslation();
  const validationMessage = useMemo(() => validationMessages(t), [t]);

  const addHandler = (data) => {
    if (data.length > 0) {
      addEditForm.setFieldValue('skills', addToSkills(undefined, data[0].label, addEditForm.values.skills));
      setOpen(false);
    }
  };

  const requestSuggestions = async (value) => {
    let suggestionsResponse = [];

    if (value?.length >= 3) {
      const recievedSuggestions = await api.get(`v1/skills?keyword=${value || ''}`);

      if (recievedSuggestions?.data?.Data.length) {
        suggestionsResponse = recievedSuggestions;
      } else if (value?.length <= maxSymbolQTY.SKILL) {
        suggestionsResponse = {
          data: {
            Data: [{ Name: value, Id: 0 }]
          }
        };
      }
    } else {
      suggestionsResponse = [];
    }

    setSuggestions(
      suggestionsResponse?.data?.Data.map((suggestion) => ({ label: suggestion?.Name, value: suggestion?.Id }))
    );
  };

  useEffect(() => {
    let active = true;
    if (!loading) {
      return undefined;
    }
    if (active) {
      requestSuggestions();
    }
    return () => {
      active = false;
    };
  }, [loading]);

  useEffect(() => {
    if (!open) {
      setSuggestions([]);
    }
  }, [open]);

  return (
    <>
      <span className={`${classes.constantText} mb-3`}>{t('common.forms.labels.skills')}</span>
      {getQtyTopSkills() < minQtyTopSkills && (
        <div className={`${classes.warning} mb-3`}>
          <span className={classes.primary}>{t('profile.builder.topremaining')}: </span>
          <span className={classes.secondary}>{validationMessage.TOP_SKILLS_MIN}</span>
        </div>
      )}
      {getQtyTopSkills() === qtyTopSkills && (
        <div className={`${classes.warning} mb-3`}>
          <span className={classes.primary}>{t('profile.builder.topremaining')}: </span>
          <span className={classes.secondary}>{validationMessage.TOP_SKILLS_MAX}</span>
        </div>
      )}
      <div className={`${classes.skillsList} mb-5`}>
        {addEditForm.values.skills?.map((item) => (
          <ThemeProvider key={item.Id} theme={theme}>
            <Chip
              className={classes.chip}
              data-testid="chip"
              label={item.SkillName}
              deleteIcon={item.TopSkill ? <StarRateIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
              color="primary"
              variant={item.TopSkill ? 'filled' : 'outlined'}
              onClick={() =>
                addEditForm.setFieldValue(
                  'skills',
                  addEditForm.values.skills.map((skill) =>
                    skill.Id !== item.Id
                      ? skill
                      : {
                          ...skill,
                          TopSkill:
                            getQtyTopSkills() >= qtyTopSkills && !skill.TopSkill ? skill.TopSkill : !skill.TopSkill
                        }
                  )
                )
              }
              onDelete={() =>
                addEditForm.setFieldValue(
                  'skills',
                  item.TopSkill
                    ? addEditForm.values.skills.map((skill) =>
                        skill.Id !== item.Id ? skill : { ...skill, TopSkill: !skill.TopSkill }
                      )
                    : addEditForm.values.skills.filter((skill) => skill.Id !== item.Id)
                )
              }
              sx={{
                '& .MuiChip-deleteIcon': {
                  color: item.TopSkill ? 'white' : '#0194D3'
                }
              }}
            />
          </ThemeProvider>
        ))}
      </div>
      <div className="row">
        <div className="d-flex flex-column col-md-8">
          <Autocomplete
            multiple
            disableCloseOnSelect
            disablePortal
            id="combo-box-demo"
            value={valuesToAdd}
            open={open}
            onOpen={() => {
              setOpen(true);
            }}
            onClose={() => {
              setOpen(false);
            }}
            freeSolo
            options={suggestions || []}
            loading={loading}
            getOptionLabel={(option) => (option.label ? option.label?.toString() : undefined)}
            onInputChange={(event, value, reason) => {
              if (reason === 'input') {
                setSkillsFieldTouched(true);
                setSkillSymbolQty(value.length);
                requestSuggestions(value);
              }
              if (reason === 'clear') {
                setSkillSymbolQty(0);
              }
            }}
            renderOption={(params, option) => {
              return (
                !addEditForm.values.skills.some((opt) => opt.SkillName === option.label) && (
                  <li {...params} className="d-flex justify-content-between align-items-center ms-3 me-1">
                    {option.label}
                  </li>
                )
              );
            }}
            renderInput={(params) => (
              <div className={classes.autocompleteInput}>
                {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                <TextField
                  {...params}
                  label={t('profile.builder.addskills')}
                  variant="standard"
                  data-testid="search-field"
                  onBlur={() => setSkillsFieldTouched(false)}
                  onFocus={() => setSkillsFieldTouched(true)}
                  InputProps={{
                    ...params.InputProps,
                    onChange: (e) => {
                      setSkillSymbolQty(e.target.value.length);
                    },
                    onKeyDown: (e) => {
                      if (e.key === 'Enter' && params.inputProps.value.length <= maxSymbolQTY.SKILL) {
                        setOpen(false);
                      }
                      e.stopPropagation();
                    },
                    endAdornment: (
                      <>
                        {loading ? (
                          <CircularProgress data-test="loading-spinner" role="progressbar" color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                  sx={{ maxWidth: 600 }}
                  value={params.inputProps.value}
                />
                {params.inputProps.value.length < 3 && skillsFieldTouched && (
                  <div className={classes.infoMessage}>
                    <ValidationErrorLabel message={validationMessage.SKILLS_MIN_CHAR_SEARCH} />
                  </div>
                )}
              </div>
            )}
            onChange={(event, value) => {
              setValuesToAdd([]);
              setSkillSymbolQty(value.length);
              addHandler(value);
            }}
            variant="standard"
            sx={{
              display: 'inline-block',
              minWidth: 300,
              borderBottom: '1px solid #0194D3'
            }}
          />
          <ValidationErrorLabel
            color={skillSymbolQty > maxSymbolQTY.SKILL ? 'red' : 'black'}
            message={`${skillSymbolQty}/${maxSymbolQTY.SKILL}`}
          />
        </div>
      </div>
    </>
  );
}

export default SkillsForm;
